import {
  Box,
  Checkbox,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, isNil, filter as lodashFilter, map, toLower } from 'lodash';
import visuallyHidden from '@mui/utils/visuallyHidden';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { SortOrder } from 'enums/common.enum';
import {
  listCentralFillQueueByPage,
  updateCompoundingQueueSort,
} from 'store/thunks/workQueueThunks';
import { casingFormat } from 'common/utils';
import formatDrugLabel from 'common/utils/formatDrugLabel';

import { RECALLABLE_STATUSES } from '../constants/centralFillEligibility';
import CentralFillQueueStatusChip from './CentralFillQueueStatusChip';
import CentralFillQueueOptions from './CentralFillQueueOptions';

const headCells = [
  { id: 'orderId', label: 'Order ID', sortable: true },
  { id: 'patientName', label: 'Patient Name', sortable: true },
  { id: 'drugName', label: 'Drug Name', sortable: true },
  // Not sortable: compoundingOrders has no ndc sort key.
  { id: 'ndc', label: 'NDC', sortable: false },
  { id: 'partnerName', label: 'Partner', sortable: false },
  { id: 'compoundingPharmacy', label: 'Compounding Pharmacy', sortable: false },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'errorDetail', label: 'Error Details', sortable: false },
  { id: 'submittedAt', label: 'Created At', sortable: true },
  { id: 'lastUpdatedAt', label: 'Last Updated', sortable: true },
];

const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    return format(new Date(value), 'MM/dd/yyyy hh:mm a');
  } catch {
    return 'N/A';
  }
};

const CentralFillQueueTable = (props) => {
  // POM-5455: eligibility is parameterised so the same table/selection serves
  // both Recall and Force Return. Defaults (see defaultProps) preserve the
  // original recall behaviour for any caller that doesn't pass them.
  const {
    selectedLineItemIds,
    onSelectionChange,
    eligibleStatuses,
    selectAllAriaLabel,
    ineligibleTooltip,
  } = props;
  const dispatch = useDispatch();
  const { sortBy, pages, currentPage, count } = useSelector(
    ({ workQueue }) => workQueue.compoundingQueue
  );

  const totalPages = Math.ceil(count / 25);
  const centralFillOrders = pages[currentPage];

  // Select-all only ever targets eligible rows on the current page — ineligible
  // rows can never enter the selection.
  const eligibleLineItemIds = map(
    lodashFilter(centralFillOrders, (row) => eligibleStatuses.has(row.status) && row.lineItemId),
    'lineItemId'
  );
  const selectedOnPage = lodashFilter(eligibleLineItemIds, (id) =>
    selectedLineItemIds.includes(id)
  );
  const allEligibleSelected =
    eligibleLineItemIds.length > 0 && selectedOnPage.length === eligibleLineItemIds.length;
  const someEligibleSelected = selectedOnPage.length > 0 && !allEligibleSelected;

  const handleSelectAll = () => {
    if (allEligibleSelected) {
      onSelectionChange(
        lodashFilter(selectedLineItemIds, (id) => !eligibleLineItemIds.includes(id))
      );
    } else {
      onSelectionChange([...new Set([...selectedLineItemIds, ...eligibleLineItemIds])]);
    }
  };

  const handleSelectRow = (lineItemId) => {
    if (selectedLineItemIds.includes(lineItemId)) {
      onSelectionChange(lodashFilter(selectedLineItemIds, (id) => id !== lineItemId));
    } else {
      onSelectionChange([...selectedLineItemIds, lineItemId]);
    }
  };

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      updateCompoundingQueueSort({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy.sortKey === sortKey && sortBy.sortOrder === SortOrder.ASCENDING
              ? SortOrder.DESCENDING
              : SortOrder.ASCENDING,
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    // POM-5410: selection does not survive page changes.
    onSelectionChange([]);
    dispatch(listCentralFillQueueByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid container direction='column'>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id}>
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        data-head-property={headCell.id}
                        onClick={handleSort}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
                <TableCell align='right' sx={{ py: 0 }}>
                  <Checkbox
                    color='primary'
                    size='small'
                    sx={{ py: 0 }}
                    indeterminate={someEligibleSelected}
                    checked={allEligibleSelected}
                    disabled={eligibleLineItemIds.length === 0}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': selectAllAriaLabel }}
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(centralFillOrders, (row, index) => {
                const isEligible = eligibleStatuses.has(row.status) && !!row.lineItemId;
                const isSelected = isEligible && selectedLineItemIds.includes(row.lineItemId);

                return (
                  <TableRow key={`${row.trackingId}-${index}`} hover selected={isSelected}>
                    <TableCell component='th' scope='row'>
                      <Link to={`/orders/${row.orderId}`} style={{ color: 'inherit' }}>
                        {row.orderId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {row.mpi && row.patientName ? (
                        <Link to={`/patients/${row.mpi}`} style={{ color: 'inherit' }}>
                          {row.patientName}
                        </Link>
                      ) : (
                        row.patientName || 'N/A'
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 280 }}>{formatDrugLabel(row) || 'N/A'}</TableCell>
                    {/* An NDC is only bound at fulfillment, so an empty cell is the norm here. */}
                    <TableCell>{row.ndc || '-'}</TableCell>
                    <TableCell>{casingFormat(row.partnerName) || 'N/A'}</TableCell>
                    <TableCell>{row.compoundingPharmacyName || 'N/A'}</TableCell>
                    <TableCell>
                      <CentralFillQueueStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      {row.errorDetail ? (
                        <Tooltip title={row.errorDetail} arrow>
                          <Typography variant='body2' noWrap sx={{ maxWidth: 280 }}>
                            {row.errorDetail}
                          </Typography>
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{formatDate(row.submittedAt)}</TableCell>
                    <TableCell>{formatDate(row.lastUpdatedAt)}</TableCell>
                    <TableCell align='right' sx={{ py: 0 }}>
                      <Tooltip title={isEligible ? '' : ineligibleTooltip} arrow>
                        <span>
                          <Checkbox
                            color='primary'
                            size='small'
                            sx={{ py: 0 }}
                            checked={isSelected}
                            disabled={!isEligible}
                            onChange={() => handleSelectRow(row.lineItemId)}
                            inputProps={{ 'aria-label': `select order ${row.orderId}` }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <CentralFillQueueOptions
                        orderId={row.orderId}
                        trackingId={row.trackingId}
                        status={row.status}
                        patientName={row.patientName}
                        drugName={row.drugName}
                        compoundingPharmacyName={row.compoundingPharmacyName}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {!isNil(centralFillOrders) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={12} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(centralFillOrders) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(centralFillOrders) && isEmpty(centralFillOrders) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

CentralFillQueueTable.propTypes = {
  selectedLineItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  eligibleStatuses: PropTypes.instanceOf(Set),
  selectAllAriaLabel: PropTypes.string,
  ineligibleTooltip: PropTypes.string,
};

// Defaults preserve the original Recall-only behaviour when the queue does not
// override eligibility (POM-5455).
CentralFillQueueTable.defaultProps = {
  eligibleStatuses: RECALLABLE_STATUSES,
  selectAllAriaLabel: 'select all recallable orders',
  ineligibleTooltip: 'Only accepted or recall-failed orders can be recalled',
};

export default CentralFillQueueTable;
